import { ArrowRight, Play } from 'lucide-react';

const APP_URL = 'https://app.theonestack.com';
const AMS_PATH = '/ams';
const TEAL = '#14b8a6';

const pills = [
  'Member Management',
  'Event Planning',
  'Financial Tracking',
  'LMS Built In',
  'Communications',
  'HOA / Chamber / Advocacy',
];

export function HeroSection() {
  return (
    <section className="relative flex min-h-screen items-center justify-center overflow-hidden pt-16">
      {/* Background glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 70% 50% at 50% 0%, ${TEAL}18 0%, transparent 70%)`,
        }}
      />
      {/* Decorative network lines */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute top-0 left-1/4 h-[60vh] w-[1px] animate-glow-pulse"
          style={{ background: `linear-gradient(to bottom, ${TEAL}30, transparent)`, transform: 'rotate(-8deg)', transformOrigin: 'top center' }}
        />
        <div
          className="absolute top-0 left-1/2 h-[70vh] w-[1px] animate-glow-pulse animate-delay-300"
          style={{ background: `linear-gradient(to bottom, ${TEAL}20, transparent)`, transform: 'translateX(-50%)' }}
        />
        <div
          className="absolute top-0 right-1/4 h-[60vh] w-[1px] animate-glow-pulse animate-delay-600"
          style={{ background: `linear-gradient(to bottom, ${TEAL}30, transparent)`, transform: 'rotate(8deg)', transformOrigin: 'top center' }}
        />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl px-6">
        <div className="text-center">
          {/* Badge */}
          <div className="mb-6 flex items-center justify-center gap-3 animate-fade-up">
            <span className="rounded-full border border-ams-700/50 bg-ams-950/60 px-3 py-1 text-xs text-zinc-400">
              Part of The One Stack
            </span>
          </div>

          {/* Headline */}
          <h1 className="mb-6 text-4xl font-extrabold tracking-tight animate-fade-up animate-delay-100 sm:text-5xl lg:text-7xl">
            The Complete Platform for{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(135deg, #5eead4, ${TEAL})` }}
            >
              Associations,
            </span>
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(135deg, #2dd4bf, #0d9488)` }}
            >
              HOAs & Chambers
            </span>
          </h1>

          {/* Subheadline */}
          <p className="mx-auto mb-8 max-w-2xl text-lg text-zinc-400 animate-fade-up animate-delay-200 sm:text-xl">
            Member management, events, committees, finance, LMS, and communications — everything your organization needs, in one place.
          </p>

          {/* Feature pills */}
          <div className="mb-10 flex flex-wrap items-center justify-center gap-2 animate-fade-up animate-delay-300">
            {pills.map((pill) => (
              <span
                key={pill}
                className="feature-pill rounded-full border border-ams-700/40 bg-ams-950/40 px-3.5 py-1.5 text-xs font-medium text-ams-300"
              >
                {pill}
              </span>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row animate-fade-up animate-delay-400">
            <a
              href={`${APP_URL}${AMS_PATH}`}
              className="group flex items-center gap-2 rounded-xl bg-ams-500 px-8 py-3.5 text-base font-semibold text-white transition hover:bg-ams-600"
            >
              Start Free Trial
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="#how-it-works"
              className="flex items-center gap-2 rounded-xl border border-ams-700/40 bg-ams-950/30 px-8 py-3.5 text-base font-semibold text-zinc-300 transition hover:bg-ams-950/60"
            >
              <Play className="h-4 w-4" />
              See How It Works
            </a>
          </div>
        </div>

        {/* Dashboard Mockup */}
        <div className="mt-16 animate-fade-up animate-delay-500">
          <div className="mx-auto max-w-5xl">
            <div className="mockup-window">
              <div className="mockup-titlebar">
                <div className="mockup-dot" style={{ background: '#ef4444' }} />
                <div className="mockup-dot" style={{ background: '#f59e0b' }} />
                <div className="mockup-dot" style={{ background: '#22c55e' }} />
                <span className="ml-2 text-xs text-zinc-500">TheOne AMS Dashboard</span>
              </div>
              <div className="p-6">
                {/* Stats row */}
                <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    { label: 'Active Members', value: '2,847', change: '+12%' },
                    { label: 'Events This Month', value: '8', change: '3 upcoming' },
                    { label: 'Dues Collected', value: '$42,150', change: '+8%' },
                    { label: 'Renewals Due', value: '156', change: 'Next 30 days' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="rounded-lg border border-ams-800/20 bg-ams-950/40 p-3"
                    >
                      <div className="text-[10px] text-zinc-500">{stat.label}</div>
                      <div className="text-lg font-bold text-zinc-100">{stat.value}</div>
                      <div className="text-[10px] text-ams-400">{stat.change}</div>
                    </div>
                  ))}
                </div>

                {/* Recent activity */}
                <div className="rounded-lg border border-ams-800/15 bg-ams-950/30 p-4">
                  <div className="mb-3 text-xs font-medium text-zinc-400">Recent Activity</div>
                  <div className="space-y-2">
                    {[
                      { text: 'Sarah Miller renewed annual membership', time: '2 min ago', type: 'member' },
                      { text: 'Q2 Board Meeting scheduled for Apr 22', time: '15 min ago', type: 'event' },
                      { text: 'Invoice #1847 paid — $250.00', time: '1 hr ago', type: 'finance' },
                      { text: 'Newsletter "April Update" sent to 2,413 members', time: '3 hrs ago', type: 'comm' },
                    ].map((item) => (
                      <div key={item.text} className="flex items-center gap-3 text-xs">
                        <span
                          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[9px] font-bold"
                          style={{
                            background:
                              item.type === 'member'
                                ? 'rgba(20,184,166,0.15)'
                                : item.type === 'event'
                                  ? 'rgba(99,102,241,0.15)'
                                  : item.type === 'finance'
                                    ? 'rgba(16,185,129,0.15)'
                                    : 'rgba(245,158,11,0.15)',
                            color:
                              item.type === 'member'
                                ? '#2dd4bf'
                                : item.type === 'event'
                                  ? '#818cf8'
                                  : item.type === 'finance'
                                    ? '#34d399'
                                    : '#fbbf24',
                          }}
                        >
                          {item.type === 'member' ? 'M' : item.type === 'event' ? 'E' : item.type === 'finance' ? '$' : 'C'}
                        </span>
                        <span className="flex-1 text-zinc-400">{item.text}</span>
                        <span className="text-zinc-600">{item.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
